import { notFound } from "@tanstack/react-router"
import type { StoreApi } from "zustand"

import type {
  CompanyGetSuccess,
  CompanyLocationAssociation,
  Location,
  LocationGetSuccess,
  Order,
  OrderGetSuccess,
  UnitOfMeasurement,
} from "@/graphql/codegen/graphql"
import {
  GetPurchaseOrderDocument,
  PurchaseOrderCompanyLocationAssociationsDocument,
  PurchaseOrderLocationDocument,
} from "@/graphql/codegen/graphql"
import { cachelessQueryClient, queryClient } from "@/providers/GraphqlRouterProvider"
import type { PurchaseOrderForm } from "@/screens/Procurement/PurchaseOrder/types"
import { getPurchaseOrderForm } from "@/screens/Procurement/PurchaseOrder/utils/common"
import { useUnitOfMeasurementsStore } from "@/stores/unitOfMeasurmentsStore"
import type { PurchaseOrderState } from "@/stores/usePurchaseOrderStore"
import { createPurchaseOrderStore } from "@/stores/usePurchaseOrderStore"

export const getPurchaseOrder = async (orderId: number): Promise<Order> => {
  // TODO: Consider using simplified query to fetch external id only to reduce the amount of data fetched.
  const { data, error } = await cachelessQueryClient.query(GetPurchaseOrderDocument, {
    input: { orderId },
    requestPolicy: "network-only",
  })

  if (error ?? data?.order.get.__typename === "OrderGetFailure") {
    console.error(error)
    throw notFound()
  }

  return (data?.order.get as OrderGetSuccess).order
}

const getShipFromLocation = async (locationId?: string): Promise<Location | null> => {
  if (!locationId) {
    return null
  }

  const { data, error } = await queryClient.query(PurchaseOrderLocationDocument, {
    locationId,
  })

  if (error ?? data?.location.get.__typename === "LocationGetFailure") {
    console.error(error)
    return null
  }

  return (data?.location.get as LocationGetSuccess).location
}

const getCompanyLocationAssociations = async (companyId?: string | null): Promise<CompanyLocationAssociation[]> => {
  if (!companyId) {
    return []
  }

  const { data, error } = await queryClient.query(PurchaseOrderCompanyLocationAssociationsDocument, {
    input: { companyId },
  })

  if (data?.company.get.__typename === "CompanyGetFailure" || error) {
    const errorMessage =
      data?.company.get.__typename === "CompanyGetFailure" ? data.company.get.error.message : undefined
    console.error(error, errorMessage)
    return []
  }

  return (data?.company.get as CompanyGetSuccess).company.locationsAssociations ?? []
}

interface RouteDataPreloadReturn {
  purchaseOrder: PurchaseOrderForm
  store: StoreApi<PurchaseOrderState>
  units: UnitOfMeasurement[]
  shipFromLocation: Location | null
  companyLocationAssociations: CompanyLocationAssociation[]
}

export const routeDataPreload = async ({
  params,
}: {
  params: { purchaseOrderId: string }
}): Promise<RouteDataPreloadReturn> => {
  const purchaseOrder = await getPurchaseOrder(Number(params.purchaseOrderId))
  const units = await useUnitOfMeasurementsStore.getState().getUnits()

  // Adjust the purchase order object to match the expected structure
  const purchaseOrderForm = getPurchaseOrderForm(purchaseOrder)

  // Initialize the store
  const store = createPurchaseOrderStore()

  const shipFromLocationId =
    purchaseOrder.sourceLocationAssociationDetail?.companyLocationAssociation?.shippingLocationId
  const shipFromLocation = await getShipFromLocation(shipFromLocationId)

  const companyId = purchaseOrder.orderLines?.find((orderLine) => orderLine.product?.companyId)?.product?.companyId
  const companyLocationAssociations = await getCompanyLocationAssociations(companyId)

  return {
    store,
    units,
    shipFromLocation,
    companyLocationAssociations,
    purchaseOrder: purchaseOrderForm,
  }
}
